import { ApiError, ErrorType } from '../models/ApiError';
import { defaultErrorHandler } from '../services/ErrorHandler';

/**
 * Handles different error types and provides user-friendly error messages
 * 
 * @deprecated Use ErrorHandler class instead for better reusability
 * This class is kept for backward compatibility
 */
export class ErrorTypeHandler {
  /**
   * Handle validation errors with specific field information
   */
  static handleValidationError(error: ApiError): string {
    const { details } = error;

    if (details?.missingFields && Array.isArray(details.missingFields)) {
      return `Missing required fields: ${details.missingFields.join(', ')}`;
    }

    if (details?.field && details?.value) {
      return `Invalid ${details.field}: ${details.value}`;
    }

    if (details?.field) {
      return `Invalid ${details.field}`;
    }

    // Check for specific validation messages in the error message
    if (error.message.includes('Invalid email')) {
      return 'Please enter a valid email address';
    }

    if (error.message.includes('Invalid phone')) {
      return 'Please enter a valid phone number';
    }

    return error.message || 'Invalid input. Please check your data and try again.';
  }

  /**
   * Handle authentication errors (401)
   */
  static handleAuthenticationError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('missing authorization header') || message.includes('missing token')) {
      return 'Please log in to continue';
    }

    if (message.includes('invalid credentials') || message.includes('incorrect username or password')) {
      return 'Invalid username or password';
    }

    if (message.includes('expired') || message.includes('token expired')) {
      return 'Session expired. Please log in again';
    }

    if (message.includes('invalid token') || message.includes('malformed token')) {
      return 'Invalid authentication. Please log in again';
    }

    return 'Authentication failed. Please try logging in again';
  }

  /**
   * Handle authorization errors (403)
   */
  static handleAuthorizationError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('insufficient permissions')) {
      return 'You do not have sufficient permissions to perform this action';
    }

    if (message.includes('access denied')) {
      return 'Access denied. Please contact your administrator';
    }

    return 'You do not have permission to perform this action';
  }

  /**
   * Handle not found errors (404)
   */
  static handleNotFoundError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('user')) {
      return 'User not found';
    }

    if (message.includes('order')) {
      return 'Order not found';
    }

    if (message.includes('resource')) {
      return 'The requested resource was not found';
    }

    return error.message || 'The requested item was not found';
  }

  /**
   * Handle conflict errors (409)
   */
  static handleConflictError(error: ApiError): string {
    const message = error.message.toLowerCase();

    if (message.includes('username') && message.includes('already exists')) {
      return 'This username is already taken. Please choose another.';
    }

    if (message.includes('email') && message.includes('already exists')) {
      return 'This email is already registered. Please use a different email.';
    }

    if (message.includes('already exists')) {
      return 'This resource already exists. Please use different values.';
    }

    if (message.includes('duplicate')) {
      return 'A duplicate entry was detected. Please check your data.';
    }

    return error.message || 'A conflict occurred. The resource may already exist.';
  }

  /**
   * Handle rate limit errors (429)
   */
  static handleRateLimitError(error: ApiError): string {
    const { details } = error;

    if (details?.retryAfter) {
      return `Too many requests. Please wait ${details.retryAfter} seconds and try again.`;
    }

    return 'Too many requests. Please wait a moment and try again.';
  }

  /**
   * Handle external service errors (502)
   */
  static handleExternalServiceError(error: ApiError): string {
    return 'Service temporarily unavailable. Please try again in a few moments.';
  }

  /**
   * Handle internal server errors (500)
   */
  static handleInternalError(error: ApiError): string {
    return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }

  /**
   * Handle network errors
   */
  static handleNetworkError(error: ApiError): string {
    return 'Network error. Please check your internet connection and try again.';
  }

  /**
   * Get a user-friendly error message based on the error type
   * 
   * @deprecated Use defaultErrorHandler.getUserFriendlyMessage() instead
   */
  static getUserFriendlyMessage(error: ApiError): string {
    return defaultErrorHandler.getUserFriendlyMessage(error);
  }

  /**
   * Determine if an error should trigger a logout
   * 
   * @deprecated Use defaultErrorHandler.shouldLogout() instead
   */
  static shouldLogout(error: ApiError): boolean {
    return defaultErrorHandler.shouldLogout(error);
  }

  /**
   * Determine if an error should be retried automatically
   * 
   * @deprecated Use defaultErrorHandler.shouldRetry() instead
   */
  static shouldRetry(error: ApiError): boolean {
    return defaultErrorHandler.shouldRetry(error);
  }
}

